import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Barcode, CircleAlert, CircleCheck, Download, LoaderCircle, Send, Tag, UserRound } from 'lucide-react';
import { Banner, PageHeader } from '../design/ui';
import './ColissimoLabels.css';

// Colissimo return label of a ticket: load the customer (from the ticket's
// sender address or an order number), check the parcel, generate the label,
// then download it or send it to the customer as an attachment. Rendered as
// the /colissimo-labels page, and inside the ticket window (« ⋯ › Étiquette
// retour Colissimo », `embedded`). The customer data never reaches the
// console (KI-215): failures log their status code only.

const COUNTRIES = [
  ['FR', 'France (FR)'],
  ['BE', 'Belgique (BE)'],
  ['DE', 'Allemagne (DE)'],
  ['ES', 'Espagne (ES)'],
  ['IT', 'Italie (IT)'],
  ['NL', 'Pays-Bas (NL)'],
];

const ColissimoLabels = ({ prefilledTicketId = '', embedded = false }) => {
  const { t } = useTranslation();
  const [ticketId, setTicketId] = useState(prefilledTicketId);
  const [orderNumber, setOrderNumber] = useState('');
  const [generating, setGenerating] = useState(false);
  const [fetchingSenderData, setFetchingSenderData] = useState(false);
  const [fetchingOrderData, setFetchingOrderData] = useState(false);
  const [sendingAsAttachment, setSendingAsAttachment] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [popupType, setPopupType] = useState('');
  const [formData, setFormData] = useState({
    customer_first_name: '',
    customer_last_name: '',
    customer_company_name: '',
    customer_line1: '',
    customer_line2: '',
    customer_city: '',
    customer_zip_code: '',
    customer_country_code: 'FR',
    customer_phone_number: '',
    customer_email: '',
    order_number: '',
    weight: 0.5,
    product_code: 'CORE',
    insurance_value_euros: ''
  });
  const [generatedLabel, setGeneratedLabel] = useState(null);

  // Update ticketId when prefilledTicketId prop changes
  useEffect(() => {
    if (prefilledTicketId) {
      setTicketId(prefilledTicketId);
    }
  }, [prefilledTicketId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const showPopup = (message, type) => {
    setPopupMessage(message);
    setPopupType(type);
    setTimeout(() => {
      setPopupMessage('');
      setPopupType('');
    }, 5000);
  };

  const fetchCustomerDataByOrder = async () => {
    if (!orderNumber.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterOrderNumber'), 'error');
      return;
    }

    try {
      setFetchingOrderData(true);
      const token = localStorage.getItem('access_token');

      const response = await axios.get(`/api/colissimo/order/${orderNumber.trim()}/customer-data`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.success && response.data.order_data) {
        const orderData = response.data.order_data;
        setFormData((previous) => ({
          ...previous,
          customer_first_name: orderData.prenom || orderData.firstName || '',
          customer_last_name: orderData.nom || orderData.lastName || '',
          customer_company_name: orderData.societe || orderData.company || '',
          customer_line1: orderData.adresse || orderData.address || orderData.rue || '',
          customer_line2: orderData.adresse2 || orderData.address2 || orderData.complement || '',
          customer_city: orderData.ville || orderData.city || '',
          customer_zip_code: orderData.code_postal || orderData.zipCode || orderData.cp || '',
          customer_country_code: orderData.pays || orderData.country || 'FR',
          customer_phone_number: orderData.telephone || orderData.phone || orderData.tel || '',
          customer_email: orderData.email || orderData.mail || '',
          order_number: String(response.data.num_commande || orderNumber.trim())
        }));
        showPopup(t('colissimoLabels.customerInfoLoadedFromOrder'), 'success');
      } else {
        showPopup(t('colissimoLabels.noCustomerDataForOrder'), 'error');
      }
    } catch (error) {
      // Status only: the error object carries the request (order number, token) and the answer (KI-215).
      console.error('Customer data by order failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorFetchingCustomerFromOrder'), 'error');
    } finally {
      setFetchingOrderData(false);
    }
  };

  const fetchSenderAddress = async () => {
    if (!ticketId.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterTicketId'), 'error');
      return;
    }

    try {
      setFetchingSenderData(true);
      const token = localStorage.getItem('access_token');

      const response = await axios.get(`/api/colissimo/ticket/${ticketId.trim()}/sender-address`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data && response.data.sender_address) {
        const senderAddress = response.data.sender_address;
        setFormData((previous) => ({
          ...previous,
          customer_first_name: senderAddress.first_name || '',
          customer_last_name: senderAddress.last_name || '',
          customer_company_name: senderAddress.company_name || '',
          customer_line1: senderAddress.line1 || '',
          customer_line2: senderAddress.line2 || '',
          customer_city: senderAddress.city || '',
          customer_zip_code: senderAddress.zip_code || '',
          customer_country_code: senderAddress.country_code || 'FR',
          customer_phone_number: senderAddress.phone_number || '',
          customer_email: senderAddress.email || '',
          order_number: String(response.data.order_reference || response.data.tracking_number || '')
        }));
        showPopup(t('colissimoLabels.customerInfoLoaded'), 'success');
      } else {
        showPopup(t('colissimoLabels.noSenderAddressForTicket'), 'error');
      }
    } catch (error) {
      console.error('Sender address failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorFetchingCustomerInfo'), 'error');
    } finally {
      setFetchingSenderData(false);
    }
  };

  const generateLabel = async (e) => {
    e.preventDefault();

    if (!ticketId.trim()) {
      showPopup(t('colissimoLabels.pleaseEnterTicketId'), 'error');
      return;
    }

    try {
      setGenerating(true);
      const token = localStorage.getItem('access_token');

      const requestData = {
        ticket_id: ticketId.trim(),
        ...formData,
        insurance_value_euros: formData.insurance_value_euros ? parseFloat(formData.insurance_value_euros) : null
      };

      const response = await axios.post('/api/colissimo/return-label', requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setGeneratedLabel(response.data);
        showPopup(t('colissimoLabels.labelGeneratedSuccess'), 'success');
      } else {
        showPopup(response.data.error || t('colissimoLabels.failedToGenerateLabel'), 'error');
      }
    } catch (error) {
      console.error('Label generation failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.errorGeneratingLabel'), 'error');
    } finally {
      setGenerating(false);
    }
  };

  const downloadLabel = () => {
    if (generatedLabel?.presigned_url) {
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = generatedLabel.presigned_url;
      link.download = generatedLabel.filename || 'colissimo-label.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const sendLabelAsAttachment = async () => {
    if (!ticketId) {
      showPopup(t('colissimoLabels.unableToSendLabel'), 'error');
      return;
    }

    setSendingAsAttachment(true);
    try {
      const token = localStorage.getItem('access_token');

      // Use the backend endpoint to send the label as attachment
      const response = await axios.post(
        `/api/colissimo/ticket/${ticketId}/send-label-as-attachment`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        showPopup(response.data.message || t('colissimoLabels.labelSentSuccess'), 'success');
      } else {
        showPopup(t('colissimoLabels.failedToSendLabel'), 'error');
      }
    } catch (error) {
      console.error('Sending the label failed:', error.response?.status);
      showPopup(error.response?.data?.detail || t('colissimoLabels.failedToSendLabel'), 'error');
    } finally {
      setSendingAsAttachment(false);
    }
  };

  const spinner = <LoaderCircle className="col-spin" strokeWidth={2} aria-hidden="true" />;

  const input = (name, label, extra = {}) => (
    <div className="u-field">
      <label className="u-label" htmlFor={name}>{label}</label>
      <input
        type="text"
        id={name}
        name={name}
        className="u-input"
        value={formData[name]}
        onChange={handleInputChange}
        {...extra}
      />
    </div>
  );

  const feedback = popupMessage && (
    popupType === 'success' ? (
      <div className="u-banner u-banner--success col-feedback" role="status">
        <CircleCheck strokeWidth={2} aria-hidden="true" />
        <div className="u-banner__body">{popupMessage}</div>
      </div>
    ) : (
      <div className="u-banner u-banner--danger col-feedback" role="alert">
        <CircleAlert strokeWidth={2} aria-hidden="true" />
        <div className="u-banner__body">{popupMessage}</div>
      </div>
    )
  );

  const form = (
    <form id="colissimo-form" className="col-form" onSubmit={generateLabel}>
      {feedback}

      {/* Where the customer comes from: the ticket, or an order number. */}
      <section className="col-section">
        <h3 className="col-section__title">{t('colissimoLabels.ticketInformation')}</h3>
        <div className="u-field">
          <label className="u-label" htmlFor="ticket_id">{t('colissimoLabels.ticketId')}</label>
          <div className="u-inline-form">
            <input
              type="text"
              id="ticket_id"
              name="ticket_id"
              className="u-input"
              value={ticketId}
              onChange={(e) => setTicketId(e.target.value)}
              placeholder={t('colissimoLabels.enterTicketId')}
              required
            />
            <button
              type="button"
              className="u-btn u-btn--secondary"
              onClick={fetchSenderAddress}
              disabled={fetchingSenderData || !ticketId.trim()}
            >
              {fetchingSenderData ? spinner : <UserRound strokeWidth={1.8} aria-hidden="true" />}
              {fetchingSenderData ? t('colissimoLabels.loading') : t('colissimoLabels.loadCustomer')}
            </button>
          </div>
        </div>
        <div className="u-field">
          <label className="u-label" htmlFor="order_number_lookup">{t('colissimoLabels.orLoadByOrderNumber')}</label>
          <div className="u-inline-form">
            <input
              type="text"
              id="order_number_lookup"
              name="order_number_lookup"
              className="u-input"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              placeholder={t('colissimoLabels.enterOrderNumber')}
            />
            <button
              type="button"
              className="u-btn u-btn--secondary"
              onClick={fetchCustomerDataByOrder}
              disabled={fetchingOrderData || !orderNumber.trim()}
            >
              {fetchingOrderData ? spinner : <Barcode strokeWidth={1.8} aria-hidden="true" />}
              {fetchingOrderData ? t('colissimoLabels.loading') : t('colissimoLabels.loadByOrder')}
            </button>
          </div>
        </div>
      </section>

      <section className="col-section">
        <h3 className="col-section__title">{t('colissimoLabels.customerInformation')}</h3>
        <div className="u-form-grid">
          {input('customer_first_name', t('colissimoLabels.firstName'))}
          {input('customer_last_name', t('colissimoLabels.lastName'))}
          {input('customer_company_name', t('colissimoLabels.companyNameOptional'))}
          {input('customer_email', t('colissimoLabels.email'), { type: 'email' })}
          {input('customer_line1', t('colissimoLabels.addressLine1'))}
          {input('customer_line2', t('colissimoLabels.addressLine2Optional'))}
          {input('customer_zip_code', t('colissimoLabels.zipCode'))}
          {input('customer_city', t('colissimoLabels.city'))}
          <div className="u-field">
            <label className="u-label" htmlFor="customer_country_code">{t('colissimoLabels.countryCode')}</label>
            <select
              id="customer_country_code"
              name="customer_country_code"
              className="u-select"
              value={formData.customer_country_code}
              onChange={handleInputChange}
            >
              {COUNTRIES.map(([code, label]) => (
                <option key={code} value={code}>{t(`ticketWorkspaceUi.colissimo.countries.${code}`, label)}</option>
              ))}
              {!COUNTRIES.some(([code]) => code === formData.customer_country_code) && (
                <option value={formData.customer_country_code}>{formData.customer_country_code}</option>
              )}
            </select>
          </div>
          {input('customer_phone_number', t('colissimoLabels.phoneNumber'), { type: 'tel' })}
        </div>
      </section>

      <section className="col-section">
        <h3 className="col-section__title">{t('colissimoLabels.packageInformation')}</h3>
        <div className="u-form-grid">
          {input('order_number', t('colissimoLabels.orderNumber'))}
          {input('weight', t('colissimoLabels.weightKg'), { type: 'number', min: '0.1', max: '30', step: '0.1' })}
          <div className="u-field">
            <label className="u-label" htmlFor="product_code">{t('colissimoLabels.productCode')}</label>
            <select
              id="product_code"
              name="product_code"
              className="u-select"
              value={formData.product_code}
              onChange={handleInputChange}
            >
              <option value="CORE">CORE</option>
              <option value="CORI">CORI</option>
              <option value="COLR">COLR</option>
            </select>
          </div>
          {input('insurance_value_euros', t('colissimoLabels.insuranceValueOptional'), {
            type: 'number', min: '0', step: '0.01', placeholder: t('colissimoLabels.leaveEmptyNoInsurance'),
          })}
        </div>
      </section>

      <div className="col-actions">
        <button type="submit" className="u-btn u-btn--primary" disabled={generating}>
          {generating ? spinner : <Tag strokeWidth={1.8} aria-hidden="true" />}
          {generating ? t('colissimoLabels.generating') : t('colissimoLabels.generateReturnLabel')}
        </button>
      </div>

      {generatedLabel && (
        <section className="col-result" aria-label={t('colissimoLabels.generatedLabel')}>
          <h3 className="col-section__title">{t('colissimoLabels.generatedLabel')}</h3>
          <dl className="col-result__facts">
            {generatedLabel.tracking_number && (
              <div>
                <dt>{t('colissimoLabels.trackingNumber')}</dt>
                <dd><code className="u-code">{generatedLabel.tracking_number}</code></dd>
              </div>
            )}
            {generatedLabel.filename && (
              <div>
                <dt>{t('colissimoLabels.filename')}</dt>
                <dd>{generatedLabel.filename}</dd>
              </div>
            )}
            {generatedLabel.message && (
              <div>
                <dt>{t('colissimoLabels.message')}</dt>
                <dd>{generatedLabel.message}</dd>
              </div>
            )}
          </dl>
          <div className="col-actions">
            <button type="button" className="u-btn u-btn--secondary" onClick={downloadLabel}>
              <Download strokeWidth={1.8} aria-hidden="true" />
              {t('colissimoLabels.downloadLabel')}
            </button>
            <button type="button" className="u-btn u-btn--primary" onClick={sendLabelAsAttachment} disabled={sendingAsAttachment}>
              {sendingAsAttachment ? spinner : <Send strokeWidth={1.8} aria-hidden="true" />}
              {sendingAsAttachment ? t('colissimoLabels.sending') : t('colissimoLabels.sendAsAttachment')}
            </button>
          </div>
        </section>
      )}
    </form>
  );

  if (embedded) {
    return <div className="col col--embedded">{form}</div>;
  }

  return (
    <div className="u-page u-page--narrow col">
      <PageHeader
        title={t('colissimoLabels.title')}
        subtitle={t('ticketWorkspaceUi.colissimo.subtitle', 'Créez l’étiquette retour d’un ticket, puis téléchargez-la ou envoyez-la au client.')}
      />
      <div className="u-well">
        <Banner tone="neutral">
          {t('ticketWorkspaceUi.colissimo.fromTicket', 'Depuis un ticket, la même étiquette se crée par « ⋯ › Étiquette retour Colissimo », sans recopier son identifiant.')}
        </Banner>
        <section className="u-card col-card">{form}</section>
      </div>
    </div>
  );
};

export default ColissimoLabels;
